/**
 * SysOps Panel API client factory.
 *
 * Builds a single `@retail-ops/api-client` `ApiClient` wired against the
 * mypcb-tenant MSAL token + base URL. NO Teams SDK — SysOps is browser-only.
 *
 * Auth is bearer-only (`Authorization: Bearer <msal>`). NO function keys are
 * shipped in the browser bundle, per the owner security directive.
 */

import { ApiClient } from '@retail-ops/api-client';
import { getMsalToken } from '@/auth/msal';
import { APP_VERSION } from '@/constants/version';

const CLIENT_ID = 'sysops-panel';
const CLIENT_VERSION = `sysops-panel@${APP_VERSION}`;

export interface ApiClientFactoryOptions {
  baseUrl: string;
  clientId?: string;
}

export function createApiClient(options: ApiClientFactoryOptions): ApiClient {
  const clientId = options.clientId ?? CLIENT_ID;
  return new ApiClient({
    baseUrl: options.baseUrl,
    // Bearer-only: no function key is shipped in the browser bundle (owner
    // security directive). The shared ApiClientConfig still types
    // `functionKey` as required, so we satisfy it with an empty string —
    // the panel authenticates exclusively via `Authorization: Bearer <msal>`.
    functionKey: '',
    version: CLIENT_VERSION,
    getToken: async () => {
      try {
        return await getMsalToken();
      } catch {
        return undefined;
      }
    },
    context: () => ({ clientId }),
  });
}

export function apiClientFactoryOptionsFromEnv(): ApiClientFactoryOptions {
  const baseUrl =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error(
      '[sysops-panel] VITE_API_BASE_URL is not set. The app cannot reach the Function.',
    );
  }

  return {
    baseUrl: baseUrl || 'https://pcb-retail-api.azurewebsites.net/api',
  };
}
